import React, { useState, useMemo, useEffect } from 'react'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Grid3x3,
  Clock,
  AlertCircle,
  MoreHorizontal
} from 'lucide-react'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addDays,
  parseISO
} from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { ContentCalendarEvent, Platform } from '@/types/social'
import PostCreateDialog from '@/features/social/components/modals/PostCreateDialog'
import { usePosts } from '@/features/social/hooks'
import { useIsMobile } from '@/hooks/use-mobile'

// Helper to determine event color based on status/type
const getEventColor = (status: string | undefined, type: string) => {
  if (type === 'holiday') return 'bg-chart-3'
  if (type === 'campaign') return 'bg-chart-4'

  switch (status) {
    case 'PUBLISHED':
    case 'POSTED':
      return 'bg-green-500'
    case 'SCHEDULED':
      return 'bg-blue-500'
    case 'DRAFT':
      return 'bg-gray-500'
    case 'FAILED':
    case 'ERROR':
      return 'bg-red-500'
    default:
      return 'bg-chart-1'
  }
}

const HOLIDAY_EVENTS: ContentCalendarEvent[] = [
  {
    id: 'h1',
    title: 'Family Day',
    date: '2025-02-17',
    type: 'holiday',
    description: 'Canadian Statutory Holiday',
    color: 'bg-purple-500'
  }
]

export default function ContentCalendar() {
  const isMobile = useIsMobile()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'list'>('month')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  // Default to list view on mobile for better UX
  useEffect(() => {
    if (isMobile && viewMode === 'month') {
      setViewMode('list')
    }
  }, [isMobile])

  // Calculate date range for the current view to fetch relevant posts
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)

  // Fetch real posts
  const { data: posts = [] } = usePosts({
    date_range: {
      start: startOfWeek(monthStart).toISOString(),
      end: endOfWeek(monthEnd).toISOString()
    }
  })

  // Transform posts into calendar events
  const events: ContentCalendarEvent[] = useMemo(() => {
    return posts.map(post => {
      // Determine the display date: scheduled_time > published_at > created_at
      const dateStr = post.scheduled_time || post.published_at || post.created_at
      const date = dateStr ? parseISO(dateStr) : new Date()

      return {
        id: post.id,
        title: post.caption ? (post.caption.length > 30 ? post.caption.substring(0, 30) + '...' : post.caption) : 'Untitled Post',
        date: format(date, 'yyyy-MM-dd'),
        type: 'post',
        platform: (post.platform_specific_data?.platform as Platform) || 'INSTAGRAM', // Default to Instagram if unknown
        status: post.status,
        post_id: post.id,
        description: post.content || post.caption || '',
        color: getEventColor(post.status, 'post')
      }
    })
  }, [posts])

  // Add some mock holidays for context (optional, could be fetched from an API)
  const allEvents = useMemo(() => [...events, ...HOLIDAY_EVENTS], [events])

  // Calendar calculations
  const calendarStart = startOfWeek(monthStart)
  const calendarEnd = endOfWeek(monthEnd)

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd
  })

  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return []
    const weekStart = startOfWeek(selectedDate || currentDate)
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  }, [selectedDate, currentDate, viewMode])

  // Filter events
  const filteredEvents = useMemo(() => {
    let filtered = allEvents

    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(event => event.platform === selectedPlatform || event.type === 'holiday')
    }

    if (searchQuery) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    return filtered
  }, [allEvents, selectedPlatform, searchQuery])

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return filteredEvents.filter(event => event.date === dateStr)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev =>
      direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
    )
  }

  const handleDateClick = (date: Date) => {
    setSelectedDate(date)
  }

  const getEventTypeIcon = (type: ContentCalendarEvent['type']) => {
    switch (type) {
      case 'post':
        return <Calendar className="h-3 w-3" />
      case 'campaign':
        return <Grid3x3 className="h-3 w-3" />
      case 'holiday':
        return <AlertCircle className="h-3 w-3" />
      case 'event':
        return <Clock className="h-3 w-3" />
      default:
        return <Calendar className="h-3 w-3" />
    }
  }

  const renderCalendarDay = (date: Date) => {
    const dayEvents = getEventsForDate(date)
    const isToday = isSameDay(date, new Date())
    const isCurrentMonth = isSameMonth(date, currentDate)
    const isSelected = selectedDate && isSameDay(date, selectedDate)

    return (
      <div
        key={date.toISOString()}
        className={`
          min-h-[80px] md:min-h-[120px] p-2 border-r border-b cursor-pointer transition-colors
          ${!isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : 'hover:bg-accent/50'}
          ${isSelected ? 'bg-primary/10 border-primary' : ''}
        `}
        onClick={() => handleDateClick(date)}
      >
        <div className="flex items-center justify-between mb-1 md:mb-2">
          <span className={`
            text-xs md:text-sm font-medium
            ${isToday ? 'bg-primary text-primary-foreground rounded-full w-5 h-5 md:w-6 md:h-6 flex items-center justify-center' : ''}
          `}>
            <span className="md:hidden">{format(date, 'EEE')} </span>
            {format(date, 'd')}
          </span>
          {dayEvents.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {dayEvents.length}
            </Badge>
          )}
        </div>

        <div className="space-y-1">
          {dayEvents.slice(0, 3).map(event => (
            <div
              key={event.id}
              className={`
                text-xs p-1 rounded text-white truncate
                ${event.color || 'bg-muted-foreground'}
              `}
              title={event.title}
            >
              <div className="flex items-center gap-1">
                {getEventTypeIcon(event.type)}
                <span className="truncate">{event.title}</span>
              </div>
            </div>
          ))}
          {dayEvents.length > 3 && (
            <div className="text-xs text-muted-foreground">
              +{dayEvents.length - 3} more
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderWeekView = () => {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-sm font-medium text-center p-2 border-b hidden md:block">
            {day}
          </div>
        ))}
        {weekDays.map(date => {
          const dayEvents = getEventsForDate(date)
          const isToday = isSameDay(date, new Date())

          return (
            <div key={date.toISOString()} className="min-h-[150px] md:min-h-[300px] p-2 md:p-3 border rounded-lg md:border-0 md:border-r bg-card md:bg-transparent">
              <div className={`
                text-base md:text-lg font-semibold mb-2 md:mb-3
                ${isToday ? 'text-primary' : ''}
              `}>
                <span className="md:hidden">{format(date, 'EEE')} </span>
                {format(date, 'd')}
              </div>
              <div className="space-y-2">
                {dayEvents.map(event => (
                  <div
                    key={event.id}
                    className={`
                      p-2 rounded text-white text-sm
                      ${event.color || 'bg-muted-foreground'}
                    `}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {getEventTypeIcon(event.type)}
                      <span className="font-medium">{event.title}</span>
                    </div>
                    {event.description && (
                      <p className="text-xs opacity-90 line-clamp-2">{event.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderListView = () => {
    const sortedEvents = [...filteredEvents].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    if (sortedEvents.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No events found for this period.</p>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {sortedEvents.map(event => (
          <Card key={event.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${event.color || 'bg-muted-foreground'}`} />
                  <div>
                    <h3 className="font-medium">{event.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}
                    </p>
                    {event.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {event.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {event.type}
                  </Badge>
                  {event.platform && (
                    <Badge variant="secondary">
                      {event.platform}
                    </Badge>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem>Edit</DropdownMenuItem>
                      <DropdownMenuItem>Duplicate</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive">
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Calendar</h1>
          <p className="text-muted-foreground">
            Plan and schedule your social media content across all platforms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Schedule Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <PostCreateDialog onClose={() => setIsCreateDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth('prev')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold min-w-[160px] text-center">
                  {format(currentDate, 'MMMM yyyy')}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth('next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>

              <Select value={selectedPlatform} onValueChange={(value) => setSelectedPlatform(value as Platform | 'all')}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="INSTAGRAM">Instagram</SelectItem>
                  <SelectItem value="FACEBOOK">Facebook</SelectItem>
                  <SelectItem value="TWITTER">Twitter</SelectItem>
                  <SelectItem value="LINKEDIN">LinkedIn</SelectItem>
                </SelectContent>
              </Select>

              <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'month' | 'week' | 'list')}>
                <TabsList>
                  <TabsTrigger value="month">Month</TabsTrigger>
                  <TabsTrigger value="week">Week</TabsTrigger>
                  <TabsTrigger value="list">List</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'month' && (
            <div className="border rounded-lg overflow-hidden">
              {/* Calendar Header - hidden on mobile, show full week on tablet+ */}
              <div className="hidden md:grid grid-cols-7 bg-muted">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-3 text-sm font-medium text-center border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Mobile hint to use list view */}
              <div className="md:hidden p-4 bg-muted/50 text-center text-sm text-muted-foreground">
                <span>Showing calendar grid. </span>
                <button
                  className="text-primary underline"
                  onClick={() => setViewMode('list')}
                >
                  Switch to List View
                </button>
                <span> for better mobile experience.</span>
              </div>

              {/* Calendar Grid - responsive columns */}
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7">
                {calendarDays.map(renderCalendarDay)}
              </div>
            </div>
          )}

          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'list' && renderListView()}
        </CardContent>
      </Card>

      {/* Selected Date Details */}
      {selectedDate && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Events for {format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(null)}
              >
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getEventsForDate(selectedDate).length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No events scheduled for this date</p>
                <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Post
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {getEventsForDate(selectedDate).map(event => (
                  <Card key={event.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${event.color || 'bg-gray-500'}`} />
                        <div className="flex-1">
                          <h3 className="font-medium">{event.title}</h3>
                          {event.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">{event.type}</Badge>
                            {event.platform && (
                              <Badge variant="secondary">{event.platform}</Badge>
                            )}
                            {event.status && (
                              <Badge variant={event.status === 'PUBLISHED' ? 'default' : 'secondary'}>
                                {event.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
